import { Box } from "@mui/material";
import CustomButton from "components/common/CustomButton";
import FileSearch from "components/fileStorage/FileSearch";
import type { PlatformEnumOption } from "features/integrationsBeta/enumLabel";
import type { ClientRunStatus } from "features/integrationsBeta/integrationsBeta";
import {
  CLIENT_FACING_TIMEZONE_DISPLAY_MODES,
  type TimezoneDisplayMode,
} from "features/integrationsBeta/timezoneDisplay";
import DateRangeSelect, {
  FILTER_FIELD_H,
  FILTER_FIELD_W,
  PAST_PRESETS,
} from "./DateRangeSelect";
import FilterSelect from "./FilterSelect";
import TimezoneDisplayToggle from "./TimezoneDisplayToggle";

export interface ClientIntegrationsFilters {
  last_client_status?: ClientRunStatus;
  last_run_after?: string;
  last_run_before?: string;
}

interface Props {
  filters: ClientIntegrationsFilters;
  onChange: (filters: ClientIntegrationsFilters) => void;
  search: string;
  onSearch: (term: string | null) => void;
  /** Client-facing statuses (GET /runs/client-statuses/). */
  statuses: PlatformEnumOption[];
  timezoneMode: TimezoneDisplayMode;
  onTimezoneModeChange: (mode: TimezoneDisplayMode) => void;
}

/**
 * Filters of the client's integrations list: name search, the client-facing
 * status of the latest run and its start date, plus the Local / UTC switch.
 * Nothing here asks an internal catalog.
 */
export default function ClientIntegrationsFilterBar({
  filters,
  onChange,
  search,
  onSearch,
  statuses,
  timezoneMode,
  onTimezoneModeChange,
}: Props) {
  const set = (patch: Partial<ClientIntegrationsFilters>) =>
    onChange({ ...filters, ...patch });

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        alignItems: "center",
      }}
    >
      <Box sx={{ width: FILTER_FIELD_W }}>
        <FileSearch
          onSearch={onSearch}
          label="Search"
          size="small"
          initialValue={search}
          fullWidth
        />
      </Box>

      <FilterSelect
        label="Status"
        value={filters.last_client_status ?? ""}
        options={statuses}
        onChange={(value) =>
          set({
            last_client_status: (value || undefined) as
              | ClientRunStatus
              | undefined,
          })
        }
      />
      <DateRangeSelect
        label="Last run"
        presets={PAST_PRESETS}
        value={{
          after: filters.last_run_after,
          before: filters.last_run_before,
        }}
        onChange={(range) =>
          set({ last_run_after: range.after, last_run_before: range.before })
        }
      />

      <CustomButton fullWidth={false} onClick={() => onChange({})}>
        Clear Filters
      </CustomButton>

      <Box sx={{ flexGrow: 1 }} />

      <TimezoneDisplayToggle
        mode={timezoneMode}
        onChange={onTimezoneModeChange}
        height={FILTER_FIELD_H}
        modes={CLIENT_FACING_TIMEZONE_DISPLAY_MODES}
      />
    </Box>
  );
}
